/**
 * Referrer Programs & Commission Rates editor — pure state helpers.
 *
 * Owner's rule (2026-10-13): UNSET is not ZERO. A program's commission
 * rate is SET only when its key exists in the rates map with a numeric
 * value; an explicit 0 is a valid SET rate (approval may proceed, Add
 * Candidate is allowed, it simply accrues nothing). These helpers keep
 * the admin editor honest about that distinction:
 * - an UNSET rate renders as an EMPTY input ("Not set" placeholder),
 *   never as a phantom 0;
 * - clearing an input REMOVES the key (persists UNSET), it never
 *   writes 0;
 * - removing a program drops its rate key from the editing state, so
 *   the save payload carries no residue for unassigned programs (the
 *   server prunes again on write — see pruneCommissionRatesToAssigned
 *   in serverCommissionGates.ts — and programRateHistory is append-only
 *   evidence that neither side modifies on removal).
 */

type RateMap = Record<string, any>;

/** The value to SHOW in a program's rate input: '' when UNSET. */
export function rateInputValue(rates: RateMap | null | undefined, programId: string): string {
  if (!rates || !Object.prototype.hasOwnProperty.call(rates, programId)) return '';
  const n = Number(rates[programId]);
  return isFinite(n) ? String(n) : '';
}

/** True when the program has a SET rate (an explicit 0 counts). */
export function rateIsSet(rates: RateMap | null | undefined, programId: string): boolean {
  return rateInputValue(rates, programId) !== '';
}

/**
 * Apply a raw input change. Empty (or non-numeric) input REMOVES the key
 * so the program persists as UNSET; a numeric value sets the key —
 * including an explicit 0, which stays a SET 0.
 */
export function applyRateInput(
  rates: RateMap | null | undefined,
  programId: string,
  raw: string,
): Record<string, number> {
  const next: Record<string, any> = { ...(rates || {}) };
  const trimmed = (raw ?? '').trim();
  const val = trimmed === '' ? NaN : parseFloat(trimmed);
  if (!isFinite(val)) {
    delete next[programId];
  } else {
    next[programId] = val;
  }
  return next as Record<string, number>;
}

/** Return a copy of the map without the given program's rate key. */
export function withoutProgramRate(
  rates: RateMap | null | undefined,
  programId: string,
): Record<string, number> {
  const next: Record<string, any> = { ...(rates || {}) };
  delete next[programId];
  return next as Record<string, number>;
}

/**
 * Drop rate keys for programs outside the given id set (mirrors the
 * server's pruneCommissionRatesToAssigned). Used when the editor loads
 * a referrer whose stored maps still carry residue keys from earlier
 * removals, so local state matches what the next save will persist.
 */
export function pruneRatesToAssignedPrograms(
  rates: RateMap | null | undefined,
  assignedIds: string[],
): Record<string, number> {
  if (!rates || typeof rates !== 'object') return {};
  const keep = new Set(assignedIds);
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(rates)) {
    if (keep.has(k)) out[k] = v;
  }
  return out as Record<string, number>;
}
